import React, { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
// Api
import { STORES_BY_HASHTAG } from 'api/store/queries';
// Types
import {
  StoresByHashtag,
  StoresByHashtagVariables,
} from 'api/store/types/StoresByHashtag';
import {
  UserRole,
  StoreStatus,
  SortDirection,
  StoresOrderBy,
} from 'api/graphql-global-types';
import { SortingOptions } from 'components/common/Sorting/Sorting';
// Ui
import Loader from 'ui3/Loader/Loader';
import InfiniteScrollLoader from 'ui3/InfiniteScrollLoader/InfiniteScrollLoader';
import Text from 'ui3/Text/Text';
// Components
import TagStoresList from './components/TagStoresList/TagStoresList';
import Sorting from 'components/common3/Sorting/Sorting';
import NotFound from 'components/common/NotFound/NotFound';
import InfiniteScroll from 'components/common3/InfiniteScroll/InfiniteScroll';

import styles from './TagStores.module.scss';

const PER_PAGE = 16;

type TagStoresProps = {
  tagName: string;
};

export const TagStores = ({ tagName }: TagStoresProps) => {
  const [sorting, setSorting] = useState<SortingOptions>({
    direction: SortDirection.ASC,
    order: StoresOrderBy.storeName,
  });

  const inputVariables = useMemo(
    () => ({
      limit: PER_PAGE,
      offset: 0,
      orderBy: sorting.order,
      direction: sorting.direction,
      status: StoreStatus.Active,
      hashtagName: tagName,
      storeRoles: [
        UserRole.Athlete,
        UserRole.Organization,
        UserRole.ContentCreator,
      ],
    }),
    [sorting, tagName]
  );

  const { data, loading, fetchMore } = useQuery<
    StoresByHashtag,
    StoresByHashtagVariables
  >(STORES_BY_HASHTAG, {
    variables: { input: { ...inputVariables } },
  });

  const fetchMoreData = () => {
    const prevOffset = data?.storesByHashtag?.offset || 0;
    const offset = prevOffset ? prevOffset + PER_PAGE : PER_PAGE;

    fetchMore({
      variables: {
        input: {
          ...inputVariables,
          offset,
        },
      },
    });
  };

  const stores = data?.storesByHashtag?.entities || [];
  const total = data?.storesByHashtag?.total;
  const hasMore = data ? stores.length !== total : false;
  const isNotFound = !loading && total === 0;

  return (
    <div className={styles.tagStoresWrapper}>
      <div className={styles.tagStoresContentHeader}>
        <Text variant="h4">{`Filtering by #${tagName}`}</Text>

        <Sorting
          wrapperClassName={styles.sectionSorting}
          sortKey="Stores"
          sorting={sorting}
          setSorting={setSorting}
        />
      </div>

      <div className={styles.tagStoresContentWrapper}>
        {loading && <Loader loading size="xLarge" />}

        <InfiniteScroll
          dataLength={stores.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={<InfiniteScrollLoader />}
          isOverflowVisible
        >
          {isNotFound && (
            <NotFound title={`Stores with "${tagName}" hashtag not found`} />
          )}
          <TagStoresList stores={stores} />
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default TagStores;
